import {defineStore} from "pinia";
import {defaultTake, type PaginationItems} from "@/models/pagination";
import {
    DefaultOrganization,
    type Organization,
    type OrganizationListModel,
    type OrganizationListRequest
} from "@/models/administration/organization/organization";
import {DefaultDrawer, type Drawer} from "@/models/drawer";
import {
    createOrganization,
    getOrganization,
    listOrganizations,
    setupDataExeConnection,
    updateOrganization
} from "@/services/administration/organization/administration-organization-service";

interface AdministrationOrganizationState {
    loading: boolean;
    organizations: PaginationItems<OrganizationListModel>;
    organization: Organization;
    organizationListRequest: OrganizationListRequest;
    detailDrawer: Drawer;
}

export const useAdministrationOrganizationStore = defineStore({
    id: 'administration-organization',
    state: () => ({
        organizations: {
            items: [],
            totalItems: 0,
            pageCount: 0
        },
        organization: DefaultOrganization(),
        organizationListRequest: {
            page: 1,
            take: defaultTake,
        },
        loading: false,
        detailDrawer: DefaultDrawer()
    } as AdministrationOrganizationState),
    actions: {
        async fetchOrganizations() {
            this.loading = true;
            const response = await listOrganizations(this.organizationListRequest);
            if (response.status == 200) {
                this.organizations = response.data;
            }
            this.loading = false;
        },
        async fetchAndAppendOrganizations() {
            this.loading = true;

            const response = await listOrganizations(this.organizationListRequest);
            if (response.status === 200) {
                const retrievedOrganizations = response.data.items;
                const allOrganizations = [...this.organizations.items, ...retrievedOrganizations];

                this.organizations.items = allOrganizations.filter((organization, index, self) =>
                    index === self.findIndex((o) => o.id === organization.id)
                );

                this.organizations.totalItems = response.data.totalItems;
                this.organizations.pageCount = response.data.pageCount;
            }

            this.loading = false;
        },
        async openUpdateDrawer(id: number) {
            this.detailDrawer.loading = true;
            this.detailDrawer.visible = true;

            const response = await getOrganization(id);
            if (response.status == 200) {
                this.organization = response.data;
            } else {
                this.detailDrawer.visible = false;
            }
            this.detailDrawer.loading = false;
        },
        async openCreateDrawer() {
            this.organization = DefaultOrganization();
            this.detailDrawer.visible = true;
        },
        async createOrUpdateOrganization() {
            this.detailDrawer.loading = true;
            let response = undefined;
            if (this.organization.id) {
                response = await updateOrganization(this.organization);

            } else {
                response = await createOrganization(this.organization);
            }

            if (response.status == 200) {
                this.detailDrawer.visible = false;
            }
            this.detailDrawer.loading = false;

            return response;
        },
        async triggerDataExeSetup() {
            this.detailDrawer.loading = true;
            const response = await setupDataExeConnection(this.organization.id);
            if (response.status == 200) {
                this.organization.isDataExeLinked = true;
            }
            this.detailDrawer.loading = false;
            return response;
        },
    }
})
